import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import TableFilter from "src/components/TableFilter";
import ReportIncidenService from "../../../components/api/ReportIncidenService";
import { REPORT_STATUS_LIST } from "src/components/constants";

const FilteringComponent = ({
    t,
    onChange,
    categories
}) => {
    const [ tableFilters, setTableFilters ] = useState({
        analystAssigned: undefined,
        risk: undefined,
        adminCategories: undefined,
        status: undefined
    });

    const {
        isFetching: ratingsAreFetching,
        data: ratings
    } = useQuery({
        queryKey: ["gdpr-fetch-risks-ratings-list"],
        queryFn: async () => {
            const service = ReportIncidenService.getInstance();

            return await service.FetchRiskRating();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t("An error occurred while fetching risk ratings."), {
                type: "error"
            });
        }
    });

    const {
        data: analysts,
        isFetching: analystsAreFetching
    } = useQuery({
        queryKey: ["gdpr-fetch-analysts-admins"],
        queryFn: async () => {
            const service = ReportIncidenService.getInstance();
        
            return await service.FetchAnalystsAndAnalystAdmins();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t("An error occurred while fetching owners."), {
                type: "error",
            });
        }
    });

    const tableFilterTabs = useMemo(() => {
        return [
            {
                type: 'radio_list',
                name: 'analystAssigned',
                title: 'Analyst',
                icon: <i className="ri-user-line" />,
                isLoading: analystsAreFetching,
                defaultValue: tableFilters?.analystAssigned,
                data: analysts || [],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return item.first_name + " " + item.last_name
                }
            },
            {
                type: 'radio_list',
                name: 'risk',
                title: 'Risk',
                icon: <i className="ri-bar-chart-line" />,
                isLoading: ratingsAreFetching,
                defaultValue: tableFilters?.risk,
                data: ratings || [],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return t(item.name)
                }
            },
            {
                type: 'check_list',
                name: 'adminCategories',
                title: 'Category',
                icon: <i className="ri-filter-3-line" />,
                isLoading: categories.isFetching,
                defaultValue: tableFilters?.adminCategories,
                data: categories.data || [],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return t(item.title)
                }
            },
            {
                type: 'radio_list',
                name: 'status',
                title: 'Status',
                icon: <i className="ri-toggle-line" />,
                isLoading: false,
                defaultValue: tableFilters?.status,
                data: REPORT_STATUS_LIST || [],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return t(item.titleKey) 
                }
            },
        ]
    }, [
        tableFilters,
        ratingsAreFetching,
        ratings,
        analysts,
        analystsAreFetching,
        categories.isFetching,
        categories.data,
        t
    ]);

    const onTableFilterOkButtonClicked = (filters) => {
        if (filters) {
            const result = {}

            if ('status' in filters) {
                result.status = filters.status !== 'all' ? filters.status : undefined
            }

            if ('risk' in filters) {
                result.risk = filters.risk !== 'all' ? filters.risk : undefined
            }

            if ('analystAssigned' in filters) {
                result.analystAssigned = filters.analystAssigned !== 'all' ? filters.analystAssigned : undefined
            }

            result.adminCategories = filters.adminCategories?.length > 0 ? filters.adminCategories : undefined

            setTableFilters({
                ...tableFilters,
                ...result
            })
        } else {
            setTableFilters({
                adminCategories: undefined,
                status: undefined,
                risk: undefined,
                analystAssigned: undefined
            })
        }
    }

    useEffect(() => {
        onChange && onChange(tableFilters)
    }, [ 
        tableFilters,
        onChange
    ]);

    return (
        <TableFilter
            className="me-2"
            defaultActiveTab='analystAssigned'
            tabs={tableFilterTabs}
            onOkButtonClicked={
                onTableFilterOkButtonClicked
            }
        />
    )
}

export default withNamespaces()(FilteringComponent);
